import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Sites
export const getSites = () => api.get('/sites').then(r => r.data);
export const getSite = (id) => api.get(`/sites/${id}`).then(r => r.data);
export const createSite = (data) => api.post('/sites', data).then(r => r.data);
export const updateSite = (id, data) => api.put(`/sites/${id}`, data).then(r => r.data);

// Studios
export const getStudios = (siteId) => api.get('/studios', { params: siteId ? { site_id: siteId } : {} }).then(r => r.data);
export const getStudio = (id) => api.get(`/studios/${id}`).then(r => r.data);
export const updateStudio = (id, data) => api.put(`/studios/${id}`, data).then(r => r.data);
export const createStudio = (data) => api.post('/studios', data).then(r => r.data);
export const assignOccupier = (studioId, data) => api.post(`/studios/${studioId}/assign-occupier`, data).then(r => r.data);
export const unassignOccupier = (studioId) => api.post(`/studios/${studioId}/unassign`).then(r => r.data);
export const getStudioHistory = (studioId) => api.get(`/studios/${studioId}/history`).then(r => r.data);

// Enquiries
export const getEnquiries = () => api.get('/enquiries').then(r => r.data);
export const createEnquiry = (data) => api.post('/enquiries', data).then(r => r.data);
export const updateEnquiry = (id, data) => api.put(`/enquiries/${id}`, data).then(r => r.data);
export const addEnquiryNote = (id, text) => api.post(`/enquiries/${id}/notes`, { text }).then(r => r.data);
export const updateEnquiryTags = (id, tags) => api.put(`/enquiries/${id}/tags`, { tags }).then(r => r.data);
export const updateEnquiryFollowup = (id, data) => api.put(`/enquiries/${id}/follow-up`, data).then(r => r.data);
export const updateEnquiryTemperature = (id, temp) => api.put(`/enquiries/${id}/temperature`, { lead_temperature: temp }).then(r => r.data);
export const getEnrichedEnquiries = () => api.get('/enquiries/enriched').then(r => r.data);
export const getStudioMatches = (studioId) => api.get(`/studios/${studioId}/matches`).then(r => r.data);

// Viewings
export const getViewings = () => api.get('/viewings').then(r => r.data);
export const createViewing = (data) => api.post('/viewings', data).then(r => r.data);
export const completeViewing = (id, outcome, insight = {}) => api.put(`/viewings/${id}/complete`, { outcome, ...insight }).then(r => r.data);

// Reservations
export const createReservation = (data) => api.post('/reservations', data).then(r => r.data);
export const confirmReservation = (id) => api.post(`/reservations/${id}/confirm`).then(r => r.data);
export const getReservations = () => api.get('/reservations').then(r => r.data);
export const expireReservation = (id) => api.post(`/reservations/${id}/expire`).then(r => r.data);
export const sendContract = (reservationId) => api.post(`/reservations/${reservationId}/send-contract`).then(r => r.data);

// Onboarding
export const getOnboarding = (reservationId) => api.get(`/onboarding/${reservationId}`).then(r => r.data);
export const savePersonalDetails = (reservationId, data) => api.post(`/onboarding/${reservationId}/personal`, data).then(r => r.data);
export const uploadOnboardingDoc = (reservationId, file, docType) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('doc_type', docType);
  return api.post(`/onboarding/${reservationId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
export const completeOnboarding = (reservationId) => api.post(`/onboarding/${reservationId}/complete`).then(r => r.data);
export const reviewOnboarding = (reservationId, decision, notes) => api.post(`/onboarding/${reservationId}/review`, { decision, notes }).then(r => r.data);

// Contract signing
export const signContract = (contractId) => api.post(`/contracts/${contractId}/sign`).then(r => r.data);
export const markLicenceSigned = (contractId) => api.post(`/contracts/${contractId}/mark-signed`).then(r => r.data);

// Workflow actions
export const sendApplicationLink = (reservationId, data = {}) => api.post(`/reservations/${reservationId}/send-application`, data).then(r => r.data);
export const sendLicence = (reservationId) => api.post(`/reservations/${reservationId}/send-licence`).then(r => r.data);
export const requestPaymentAction = (reservationId) => api.post(`/reservations/${reservationId}/request-payment`).then(r => r.data);
export const confirmPaymentAction = (reservationId) => api.post(`/reservations/${reservationId}/confirm-payment`).then(r => r.data);
export const issueAccess = (reservationId, data) => api.post(`/reservations/${reservationId}/issue-access`, data).then(r => r.data);

// Public application (token-based)
export const getApplication = (token) => axios.get(`${API}/public/application/${token}`).then(r => r.data);
export const submitApplicationPersonal = (token, data) => axios.post(`${API}/public/application/${token}/personal`, data).then(r => r.data);
export const uploadApplicationDoc = (token, file, docType) => {
  const fd = new FormData(); fd.append('file', file); fd.append('doc_type', docType);
  return axios.post(`${API}/public/application/${token}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
export const submitApplication = (token) => axios.post(`${API}/public/application/${token}/submit`).then(r => r.data);

// Account activation
export const checkActivationToken = (token) => api.get(`/auth/activation/${token}`).then(r => r.data);
export const activateAccount = (token, password) => api.post('/auth/activate', { token, password }).then(r => r.data);

// 2FA
export const setup2faAuthenticator = () => api.post('/auth/2fa/setup-authenticator').then(r => r.data);
export const confirm2faAuthenticator = (code) => api.post('/auth/2fa/confirm-authenticator', { code }).then(r => r.data);
export const enable2faEmail = () => api.post('/auth/2fa/enable-email').then(r => r.data);
export const disable2fa = () => api.post('/auth/2fa/disable').then(r => r.data);
export const get2faStatus = () => api.get('/auth/2fa/status').then(r => r.data);

// Admin user management (super admin)
export const getAdminUsers = () => api.get('/admin/users').then(r => r.data);
export const createAdminUser = (data) => api.post('/admin/users', data).then(r => r.data);
export const updateAdminUser = (id, data) => api.put(`/admin/users/${id}`, data).then(r => r.data);
export const deleteAdminUser = (id) => api.delete(`/admin/users/${id}`).then(r => r.data);
export const getAuditLog = () => api.get('/admin/audit-log').then(r => r.data);

// Reservation auto-expiry
export const autoExpireReservations = () => api.post('/reservations/auto-expire').then(r => r.data);

// Contracts
export const getContracts = (occupierId) => api.get('/contracts', { params: occupierId ? { occupier_id: occupierId } : {} }).then(r => r.data);
export const updateContract = (id, data) => api.put(`/contracts/${id}`, data).then(r => r.data);

// Payments
export const getPayments = (occupierId) => api.get('/payments', { params: occupierId ? { occupier_id: occupierId } : {} }).then(r => r.data);
export const updatePayment = (id, data) => api.put(`/payments/${id}`, data).then(r => r.data);
export const getCreditControl = () => api.get('/payments/credit-control').then(r => r.data);
export const addPaymentNote = (id, text) => api.post(`/payments/${id}/notes`, { text }).then(r => r.data);

// Tickets
export const getTickets = () => api.get('/tickets').then(r => r.data);
export const createTicket = (data) => api.post('/tickets', data).then(r => r.data);
export const updateTicket = (id, data) => api.put(`/tickets/${id}`, data).then(r => r.data);

// Access
export const getAccess = (occupierId) => api.get('/access', { params: occupierId ? { occupier_id: occupierId } : {} }).then(r => r.data);

// Dashboard
export const getOperatorDashboard = () => api.get('/dashboard/operator').then(r => r.data);
export const getPractitionerDashboard = () => api.get('/dashboard/practitioner').then(r => r.data);

// Occupiers
export const getOccupiers = () => api.get('/occupiers').then(r => r.data);
export const updateOccupier = (id, data) => api.put(`/occupiers/${id}`, data).then(r => r.data);

// Profile
export const updateProfile = (data) => api.put('/profile', data).then(r => r.data);

// Welcome & House Rules
export const getWelcome = () => api.get('/welcome').then(r => r.data);
export const getHouseRules = () => api.get('/house-rules').then(r => r.data);

// Seed
export const seedData = () => api.post('/seed').then(r => r.data);

// Newsletter subscribers
export const subscribe = (email, page) => axios.post(`${API}/public/subscribe`, { email, page }).then(r => r.data);
export const getSubscribers = () => api.get('/admin/subscribers').then(r => r.data);
export const adminUnsubscribe = (id) => api.put(`/admin/subscribers/${id}/unsubscribe`).then(r => r.data);
export const exportSubscribersUrl = `${API}/admin/subscribers/export`;
export const getPendingNotifications = () => api.get('/admin/subscriber-notifications').then(r => r.data);

// Suppliers
export const getSuppliers = () => api.get('/suppliers').then(r => r.data);
export const createSupplier = (data) => api.post('/suppliers', data).then(r => r.data);
export const updateSupplier = (id, data) => api.put(`/suppliers/${id}`, data).then(r => r.data);

// Supplier Assignments
export const getSupplierAssignments = (siteId) => api.get('/supplier-assignments', { params: siteId ? { site_id: siteId } : {} }).then(r => r.data);
export const createSupplierAssignment = (data) => api.post('/supplier-assignments', data).then(r => r.data);

// Service Logs
export const getServiceLogs = (siteId) => api.get('/service-logs', { params: siteId ? { site_id: siteId } : {} }).then(r => r.data);
export const createServiceLog = (data) => api.post('/service-logs', data).then(r => r.data);

// Supplier Invoices
export const getSupplierInvoices = (params) => api.get('/supplier-invoices', { params }).then(r => r.data);
export const createSupplierInvoice = (data) => api.post('/supplier-invoices', data).then(r => r.data);

// Site Operations
export const getSiteOperations = (siteId) => api.get(`/site-operations/${siteId}`).then(r => r.data);

export default api;
